// Unified queue across all portals. Lists every ticket the agent has
// visibility into within the current org, sorted newest-first. Optional
// filter by portal lives on the left, so an agent who works multiple
// portals (e.g., IT + HR) can pivot without leaving the page.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Lifebuoy, Plus, GearSix, MagnifyingGlass, DownloadSimple, Rows, ListDashes } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AppFooter from '../../components/AppFooter';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';
import CreateTicketDialog from '../../components/serviceDesk/CreateTicketDialog';
import QueueSummary from '../../components/serviceDesk/QueueSummary';
import QueueViewToggle from '../../components/serviceDesk/QueueViewToggle';
import QueueStats, { STAT_PREDICATES } from '../../components/serviceDesk/QueueStats';
import QueueTable from '../../components/serviceDesk/QueueTable';
import ExportTicketsDialog from '../../components/serviceDesk/ExportTicketsDialog';
import { Dot, STATUS_DOT, PRIORITY_DOT, STATUS_OPTIONS, PRIORITY_OPTIONS, statusLabel, priorityLabel, CATEGORY_OPTIONS, categoryLabel } from '../../components/serviceDesk/ticketMeta';
import { sortTickets } from '../../components/serviceDesk/queueColumns';

const UNIFIED_COLUMNS = ['key', 'title', 'portal', 'category', 'status', 'priority', 'assignee', 'sla', 'created'];

export default function ServiceDeskQueuePage() {
  const { currentOrg, getOrgHeaders, API, user } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [portals, setPortals] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedPortal, setSelectedPortal] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [csat, setCsat] = useState(null);
  const [view, setView] = useState('list');
  const [showExport, setShowExport] = useState(false);
  const [quickFilter, setQuickFilter] = useState(null);
  const [density, setDensity] = useState(() => localStorage.getItem('sd_queue_density') || 'comfortable');

  const sdEnabled = caps?.service_desk_enabled === true;
  const canManage = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);
  const canEdit = !!currentOrg?.role;
  const setDensityPref = (d) => { setDensity(d); localStorage.setItem('sd_queue_density', d); };

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const [p, t, c, m] = await Promise.all([
        axios.get(`${API}/service-desk/portals`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/tickets`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/csat-summary`, { withCredentials: true, headers: getOrgHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/members`, { withCredentials: true, headers: getOrgHeaders() }).catch(() => ({ data: [] })),
      ]);
      setPortals(p.data || []);
      setTickets(t.data || []);
      setCsat(c.data || null);
      setMembers(m.data || []);
    } catch (e) { console.error('[ServiceDesk] load', e); }
    finally { setLoading(false); }
  }, [currentOrg, sdEnabled, API, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  const portalById = useMemo(() => Object.fromEntries(portals.map(p => [p.portal_id, p])), [portals]);
  const memberById = useMemo(() => Object.fromEntries(members.map(m => [m.user_id, m])), [members]);
  const assigneeOptions = useMemo(() => {
    const ids = [...new Set((tickets || []).map(t => t.assignee_user_id).filter(Boolean))];
    return ids.map(id => ({ id, name: memberById[id]?.name || memberById[id]?.email || 'Unknown' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tickets, memberById]);

  const onSort = (col) => {
    if (sortBy === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = tickets.filter(t => {
      if (selectedPortal !== 'all' && t.portal_id !== selectedPortal) return false;
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
      if (categoryFilter !== 'all' && (t.category || 'service_request') !== categoryFilter) return false;
      if (assigneeFilter === 'me' && t.assignee_user_id !== user?.user_id) return false;
      if (assigneeFilter === 'unassigned' && t.assignee_user_id) return false;
      if (!['all', 'me', 'unassigned'].includes(assigneeFilter) && t.assignee_user_id !== assigneeFilter) return false;
      if (q && !(`${t.ticket_key} ${t.title}`.toLowerCase().includes(q))) return false;
      if (quickFilter && !STAT_PREDICATES[quickFilter]?.(t)) return false;
      return true;
    });
    return sortTickets(list, sortBy, sortDir, { memberById, portalById });
  }, [tickets, selectedPortal, statusFilter, priorityFilter, categoryFilter, assigneeFilter, query, sortBy, sortDir, memberById, portalById, user, quickFilter]);

  const onQuickEdit = async (ticketId, action, value) => {
    const body = action === 'assignee' ? { assignee_user_id: value } : { [action]: value };
    try {
      const { data } = await axios.patch(`${API}/service-desk/tickets/${ticketId}`, body, { withCredentials: true, headers: getOrgHeaders() });
      setTickets(prev => prev.map(t => (t.ticket_id === ticketId ? { ...t, ...data } : t)));
      toast.success('Ticket updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };
  const onBulk = async (action, value, ids) => {
    try {
      const { data } = await axios.post(`${API}/service-desk/tickets/bulk`, { ticket_ids: ids, action, value }, { withCredentials: true, headers: getOrgHeaders() });
      toast.success(`Updated ${data.updated} ticket${data.updated === 1 ? '' : 's'}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk update failed'); }
  };

  // Summary scope: every ticket in the selected portal (ignores list filters
  // so the breakdown reflects the whole queue, not the filtered slice).
  const scopedTickets = useMemo(
    () => tickets.filter(t => selectedPortal === 'all' || t.portal_id === selectedPortal),
    [tickets, selectedPortal],
  );

  if (!sdEnabled) {
    return <ServiceDeskUpsell />;
  }

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="service-desk-queue-page">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
              <Lifebuoy size={14} weight="fill" /> Service Desk
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">Queue</h1>
            <p className="text-sm text-zinc-500 mt-0.5">All tickets across your service portals</p>
          </div>
          <div className="flex gap-2">
            {csat && csat.total_rated > 0 && (
              <div className="flex items-center gap-2 rounded-md bg-white ring-1 ring-zinc-200 px-3 py-1.5" data-testid="sd-csat-summary">
                <span className="text-lg font-bold text-emerald-600">{csat.csat_percent}%</span>
                <div className="leading-tight">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">CSAT</p>
                  <p className="text-[10px] text-zinc-400">{csat.total_rated} rated</p>
                </div>
              </div>
            )}
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setShowExport(true)} disabled={portals.length === 0} data-testid="sd-export-btn">
                <DownloadSimple size={14} className="mr-1.5" /> Export
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/service-desk/settings')} data-testid="sd-settings-btn">
              <GearSix size={14} className="mr-1.5" /> Settings
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)} disabled={portals.length === 0} data-testid="sd-new-ticket-btn">
              <Plus size={14} className="mr-1.5" /> New ticket
            </Button>
          </div>
        </div>

        {portals.length === 0 && !loading && (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
            <Lifebuoy size={36} className="mx-auto text-zinc-300 mb-3" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-1">No portals yet</h3>
            <p className="text-sm text-zinc-500 mb-4">Create your first service portal to start receiving requests.</p>
            <Button onClick={() => navigate('/service-desk/settings')} data-testid="sd-create-first-portal">
              Create a portal
            </Button>
          </div>
        )}

        {portals.length > 0 && (
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="sd-portal-filters">
            <button onClick={() => setSelectedPortal('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ring-1 transition-colors ${selectedPortal === 'all' ? 'bg-zinc-900 text-white ring-zinc-900' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'}`}
              data-testid="sd-portal-all">
              All portals
            </button>
            {portals.map(p => (
              <button key={p.portal_id} onClick={() => setSelectedPortal(p.portal_id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium ring-1 transition-colors ${selectedPortal === p.portal_id ? 'bg-zinc-900 text-white ring-zinc-900' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'}`}
                data-testid={`sd-portal-${p.portal_id}`}>
                {p.key} · {p.name}
              </button>
            ))}
          </div>
        )}

        {portals.length > 0 && (
          <div className="flex items-center justify-between mb-4" data-testid="sd-queue-view-toggle-row">
            <QueueViewToggle view={view} onChange={setView} />
            {view === 'summary' && (
              <span className="text-xs text-zinc-400">{scopedTickets.length} {scopedTickets.length === 1 ? 'ticket' : 'tickets'} in scope</span>
            )}
          </div>
        )}

        {portals.length > 0 && view === 'list' && (
          <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="sd-queue-toolbar">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by key or title…"
                className="w-full h-9 pl-9 pr-3 rounded-md border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="sd-search" />
            </div>
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="sd-assignee-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tickets</SelectItem>
                <SelectItem value="me">Assigned to me</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {assigneeOptions.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="sd-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s} value={s}><span className="flex items-center gap-2"><Dot className={STATUS_DOT[s]} />{statusLabel(s)}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-9 w-[145px]" data-testid="sd-priority-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {PRIORITY_OPTIONS.map(p => (
                  <SelectItem key={p} value={p}><span className="flex items-center gap-2"><Dot className={PRIORITY_DOT[p]} />{priorityLabel(p)}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9 w-[155px]" data-testid="sd-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{categoryLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-zinc-400 ml-auto">{filtered.length} {filtered.length === 1 ? 'ticket' : 'tickets'}</span>
            <div className="flex items-center rounded-md border border-zinc-200 bg-white p-0.5" data-testid="sd-density-toggle">
              <button onClick={() => setDensityPref('comfortable')} title="Comfortable"
                className={`p-1.5 rounded ${density === 'comfortable' ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-700'}`} data-testid="sd-density-comfortable">
                <Rows size={14} />
              </button>
              <button onClick={() => setDensityPref('compact')} title="Compact"
                className={`p-1.5 rounded ${density === 'compact' ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-700'}`} data-testid="sd-density-compact">
                <ListDashes size={14} />
              </button>
            </div>
          </div>
        )}

        {portals.length > 0 && view === 'list' && !loading && (
          <QueueStats tickets={scopedTickets} active={quickFilter} onPick={setQuickFilter} />
        )}

        {loading ? (
          <QueueTable loading columns={UNIFIED_COLUMNS} tickets={[]} />
        ) : view === 'summary' && portals.length > 0 ? (
          <QueueSummary tickets={scopedTickets} memberById={memberById} />
        ) : filtered.length === 0 && portals.length > 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
            <p className="text-sm text-zinc-500">No tickets match the current filter.</p>
          </div>
        ) : filtered.length > 0 ? (
          <QueueTable
            tickets={filtered}
            columns={UNIFIED_COLUMNS}
            ctx={{ memberById, portalById }}
            sortBy={sortBy} sortDir={sortDir} onSort={onSort}
            onRowClick={(t) => navigate(`/service-desk/portal/${t.portal_id}/ticket/${t.ticket_id}`)}
            members={members} memberById={memberById}
            canEdit={canEdit} onQuickEdit={onQuickEdit} onBulk={onBulk}
            density={density} loading={false}
          />
        ) : null}
      </div>
      <AppFooter />
      <CreateTicketDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        portals={portals}
        onCreated={(ticket) => {
          setShowCreate(false);
          navigate(`/service-desk/portal/${ticket.portal_id}/ticket/${ticket.ticket_id}`);
        }}
      />
      <ExportTicketsDialog
        open={showExport}
        onClose={() => setShowExport(false)}
        portalId={selectedPortal !== 'all' ? selectedPortal : null}
        portalName={selectedPortal !== 'all' ? portalById[selectedPortal]?.name : null}
        filters={{ status: statusFilter, priority: priorityFilter, category: categoryFilter, assignee: assigneeFilter, q: query, userId: user?.user_id }}
      />
    </div>
  );
}
